import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
/**
 * Pipeline de financiación por proyecto.
 *
 * Estructura: KANBAN con columnas por estado (7 estados).
 * - Cada tarjeta = entrada del pipeline con org_id + estado + datos del contacto.
 * - Catálogo global de organizaciones (`/api/organizations`) compartido entre proyectos.
 * - Búsqueda IA (Tavily + Claude) para descubrir nuevas organizaciones y añadirlas
 *   al catálogo + al pipeline con 1 click.
 */

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    .format(Number(n) || 0);

const STAGE_STYLES = {
  "Por contactar": { bg: "bg-stone-50", header: "bg-stone-200/70", text: "text-stone-700", dot: "#B8B5AD" },
  "Enviado dossier": { bg: "bg-amber-50/40", header: "bg-amber-200/60", text: "text-amber-900", dot: "#C9821B" },
  "En conversación": { bg: "bg-orange-50/40", header: "bg-orange-200/60", text: "text-orange-900", dot: "#E59C2C" },
  "Reunión": { bg: "bg-sky-50/40", header: "bg-sky-200/60", text: "text-sky-900", dot: "#5B7A8E" },
  "Comprometido": { bg: "bg-emerald-50/40", header: "bg-emerald-200/70", text: "text-emerald-900", dot: "#2E7D5B" },
  "Ganado": { bg: "bg-emerald-100/60", header: "bg-emerald-300", text: "text-emerald-900", dot: "#2E7D5B" },
  "Perdido": { bg: "bg-red-50/40", header: "bg-red-200/60", text: "text-red-900", dot: "#941620" },
};

const ORG_TYPE_ICON = {
  "Fundación": "⛨",
  "Broadcaster": "▭",
  "Plataforma": "▸",
  "Distribuidor": "⇒",
  "Sales Agent": "⌬",
  "Mecenas": "✦",
  "Fondo público": "▣",
  "Productora": "◐",
  "Coproductora": "◉",
  "Festival/Mercado": "✶",
  "Otro": "·",
};

export default function PipelineTab({ project }) {
  const [meta, setMeta] = useState({ types: [], stages: [] });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban"); // "kanban" | "list"
  const [adding, setAdding] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [m, p] = await Promise.all([
          api.get("/organizations/meta"),
          api.get(`/icaa/projects/${project.id}/pipeline`),
        ]);
        if (cancelled) return;
        setMeta(m.data);
        setEntries(p.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  // Reload helper for after mutations
  const reload = async () => {
    const p = await api.get(`/icaa/projects/${project.id}/pipeline`);
    setEntries(p.data || []);
  };

  const updateEntry = async (eid, patch) => {
    try {
      const r = await api.put(`/icaa/projects/${project.id}/pipeline/${eid}`, patch);
      setEntries((prev) => prev.map((e) => (e.id === eid ? r.data : e)));
    } catch (err) {
      alert("No se pudo actualizar: " + (err?.response?.data?.detail || err.message));
    }
  };

  const deleteEntry = async (eid, orgName) => {
    if (!window.confirm(`Quitar "${orgName}" del pipeline?`)) return;
    await api.delete(`/icaa/projects/${project.id}/pipeline/${eid}`);
    setEntries((prev) => prev.filter((e) => e.id !== eid));
  };

  const totals = useMemo(() => {
    const byStage = {};
    let estimated = 0, confirmed = 0;
    for (const e of entries) {
      byStage[e.stage] = (byStage[e.stage] || 0) + 1;
      estimated += Number(e.estimated_amount) || 0;
      confirmed += Number(e.confirmed_amount) || 0;
    }
    return { byStage, estimated, confirmed, total: entries.length };
  }, [entries]);

  return (
    <section className="mx-auto max-w-[1600px] px-4 pb-12 pt-6 sm:px-6 lg:px-10" data-testid="pipeline-tab">
      {/* Header + KPIs */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.4em] text-[#2C2B29]/55">
            Pipeline de financiación
          </p>
          <h2 className="mt-1 font-display text-2xl tracking-tight sm:text-3xl">
            Quién, en qué fase y con cuánto
          </h2>
          <p className="mt-1 text-[12px] text-stone-500">
            {totals.total} organización{totals.total !== 1 ? "es" : ""} ·
            estimación {fmtEUR(totals.estimated)} ·
            confirmado {fmtEUR(totals.confirmed)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 rounded-full bg-stone-100 p-1" data-testid="pipeline-view-toggle">
            <button
              onClick={() => setView("kanban")}
              data-testid="view-kanban"
              className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                view === "kanban" ? "bg-stone-900 text-white" : "text-stone-500"
              }`}
            >
              Tablero
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                view === "list" ? "bg-stone-900 text-white" : "text-stone-500"
              }`}
            >
              Lista
            </button>
          </div>
          <button
            onClick={() => setAdding(true)}
            data-testid="pipeline-add-btn"
            className="rounded-full bg-[#26775E] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48]"
          >
            + Añadir organización
          </button>
        </div>
      </header>

      {loading && <p className="text-center text-sm text-stone-500">Cargando pipeline…</p>}

      {!loading && entries.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-white/40 px-6 py-12 text-center">
          <p className="font-display text-xl text-stone-700">Aún no hay nadie en el pipeline</p>
          <p className="mt-2 text-[13px] text-stone-500">
            Añade fundaciones, broadcasters, distribuidores o cualquier financiador.
            Puedes <strong>buscar con IA</strong> (&quot;fundaciones documental europeo&quot;)
            o crear una a mano.
          </p>
          <button
            onClick={() => setAdding(true)}
            className="mt-5 rounded-full bg-stone-900 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#26775E]"
          >
            + Empezar
          </button>
        </div>
      )}

      {!loading && entries.length > 0 && view === "kanban" && (
        <KanbanBoard
          stages={meta.stages}
          entries={entries}
          onChangeStage={(eid, stage) => updateEntry(eid, { stage })}
          onEditEntry={(e) => setEditingEntry(e)}
          onDeleteEntry={(eid, orgName) => deleteEntry(eid, orgName)}
        />
      )}

      {!loading && entries.length > 0 && view === "list" && (
        <ListView
          stages={meta.stages}
          entries={entries}
          onChangeStage={(eid, stage) => updateEntry(eid, { stage })}
          onEditEntry={(e) => setEditingEntry(e)}
          onDeleteEntry={(eid, orgName) => deleteEntry(eid, orgName)}
        />
      )}

      {adding && (
        <AddPipelineModal
          projectId={project.id}
          orgTypes={meta.types}
          stages={meta.stages}
          existingOrgIds={new Set(entries.map((e) => e.org_id))}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); reload(); }}
        />
      )}

      {editingEntry && (
        <EditPipelineModal
          entry={editingEntry}
          stages={meta.stages}
          onClose={() => setEditingEntry(null)}
          onSave={async (patch) => {
            await updateEntry(editingEntry.id, patch);
            setEditingEntry(null);
          }}
        />
      )}
    </section>
  );
}

// ============================================================================
// Kanban board
// ============================================================================
function KanbanBoard({ stages, entries, onChangeStage, onEditEntry, onDeleteEntry }) {
  const byStage = useMemo(() => {
    const map = {};
    for (const st of stages) map[st] = [];
    for (const e of entries) (map[e.stage] || (map[e.stage] = [])).push(e);
    return map;
  }, [stages, entries]);

  const [dragId, setDragId] = useState(null);

  return (
    <div className="-mx-2 flex gap-3 overflow-x-auto px-2 pb-2" data-testid="kanban-board">
      {stages.map((st) => {
        const style = STAGE_STYLES[st] || STAGE_STYLES["Por contactar"];
        const items = byStage[st] || [];
        return (
          <div
            key={st}
            data-testid={`kanban-col-${st}`}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId) onChangeStage(dragId, st);
              setDragId(null);
            }}
            className={`flex w-72 shrink-0 flex-col rounded-xl border border-stone-200/80 ${style.bg}`}
          >
            <div className={`flex items-center justify-between rounded-t-xl px-3 py-2 ${style.header}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${style.text}`}>{st}</p>
              <span className={`rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-mono ${style.text}`}>
                {items.length}
              </span>
            </div>
            <div className="flex-1 space-y-2 p-2">
              {items.length === 0 && (
                <p className="rounded-md border border-dashed border-stone-300/60 bg-white/40 py-4 text-center text-[11px] text-stone-400">
                  vacío
                </p>
              )}
              {items.map((entry) => (
                <PipelineCard
                  key={entry.id}
                  entry={entry}
                  onDragStart={() => setDragId(entry.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onEditEntry(entry)}
                  onDelete={() => onDeleteEntry(entry.id, entry.org?.name || "Sin nombre")}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PipelineCard({ entry, onDragStart, onDragEnd, onClick, onDelete }) {
  const org = entry.org || {};
  const icon = ORG_TYPE_ICON[org.org_type] || "·";
  const initial = (org.name || "?").trim()[0]?.toUpperCase() || "?";
  return (
    <article
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`pipeline-card-${entry.id}`}
      className="group cursor-grab rounded-lg border border-gb-paper bg-white p-3 shadow-sm transition hover:border-gb-red/40 hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gb-paper-soft font-serif text-sm font-medium text-gb-black"
              title={org.org_type}>
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-serif text-[14px] leading-tight text-gb-black" title={org.name}>
            {org.name || "Sin nombre"}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-[0.18em] text-gb-muted">
            <span className="mr-1 text-gb-red">{icon}</span>
            {org.org_type || "Sin tipo"}{org.country && ` · ${org.country}`}
          </p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`pipeline-card-del-${entry.id}`}
          className="opacity-0 transition group-hover:opacity-60 hover:text-gb-red"
          title="Quitar del pipeline"
        >
          ×
        </button>
      </div>
      {(entry.estimated_amount > 0 || entry.next_action) && (
        <div className="mt-2.5 space-y-1 border-t border-gb-paper pt-2 text-[11px]">
          {entry.estimated_amount > 0 && (
            <p className="font-mono font-semibold tabular-nums text-gb-black">
              ~{fmtEUR(entry.estimated_amount)}
            </p>
          )}
          {entry.next_action && (
            <p className="line-clamp-2 text-[11px] text-gb-muted">
              <span className="text-gb-red">↪</span> {entry.next_action}{entry.next_action_at && <span className="ml-1 font-medium text-gb-black/70">· {entry.next_action_at}</span>}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ============================================================================
// List view
// ============================================================================
function ListView({ stages, entries, onChangeStage, onEditEntry, onDeleteEntry }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-testid="pipeline-list">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <tr>
            <th className="px-4 py-3">Organización</th>
            <th className="px-4 py-3">Tipo</th>
            <th className="px-4 py-3">País</th>
            <th className="px-4 py-3">Fase</th>
            <th className="px-4 py-3 text-right">Estimación</th>
            <th className="px-4 py-3">Próximo paso</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const org = e.org || {};
            return (
              <tr
                key={e.id}
                className="border-b border-stone-100 transition hover:bg-stone-50"
                data-testid={`pipeline-list-row-${e.id}`}
              >
                <td className="px-4 py-3">
                  <button onClick={() => onEditEntry(e)}
                          className="text-left font-serif text-[14px] text-stone-900 hover:text-[#26775E]">
                    {org.name}
                  </button>
                  {org.website && (
                    <a href={org.website} target="_blank" rel="noreferrer"
                       className="ml-2 text-[10px] text-stone-400 hover:text-[#26775E]">↗</a>
                  )}
                </td>
                <td className="px-4 py-3 text-[12px] text-stone-600">{org.org_type}</td>
                <td className="px-4 py-3 text-[12px] text-stone-600">{org.country || "—"}</td>
                <td className="px-4 py-3">
                  <select
                    value={e.stage}
                    onChange={(ev) => onChangeStage(e.id, ev.target.value)}
                    data-testid={`stage-select-${e.id}`}
                    className="rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                  >
                    {stages.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right font-mono text-[12px] tabular-nums text-stone-700">
                  {e.estimated_amount > 0 ? fmtEUR(e.estimated_amount) : "—"}
                </td>
                <td className="px-4 py-3 text-[12px] text-stone-600">
                  {e.next_action ? (
                    <>
                      <span>{e.next_action}</span>
                      {e.next_action_at && <span className="ml-2 text-stone-400">· {e.next_action_at}</span>}
                    </>
                  ) : <span className="text-stone-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onDeleteEntry(e.id, org.name)}
                    data-testid={`pipeline-list-del-${e.id}`}
                    className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px] text-stone-500 hover:border-red-400 hover:text-red-600"
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Add modal (with AI search)
// ============================================================================
function AddPipelineModal({ projectId, orgTypes, stages, existingOrgIds, onClose, onSaved }) {
  const [mode, setMode] = useState("search"); // "search" | "catalog" | "manual"
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:p-6"
         onClick={onClose} data-testid="pipeline-add-modal">
      <div className="my-4 flex w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">Añadir al pipeline</p>
            <h3 className="mt-0.5 font-display text-lg text-stone-900">Encuentra o crea una organización</h3>
          </div>
          <button onClick={onClose} className="rounded-full px-2 py-1 text-stone-500 hover:bg-stone-100">✕</button>
        </div>

        <div className="flex gap-1 px-3 pb-0 pt-2">
          {[
            { id: "search", label: "🔍 Buscar con IA" },
            { id: "catalog", label: "📚 Catálogo" },
            { id: "manual", label: "✎ Crear manualmente" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setMode(t.id)}
              data-testid={`add-mode-${t.id}`}
              className={`rounded-t-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-widest transition ${
                mode === t.id ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="border-t border-stone-200 px-5 py-4" style={{ minHeight: 380 }}>
          {mode === "search" && (
            <AISearchPane projectId={projectId} stages={stages} existingOrgIds={existingOrgIds} onSaved={onSaved} />
          )}
          {mode === "catalog" && (
            <CatalogPane projectId={projectId} stages={stages} existingOrgIds={existingOrgIds} onSaved={onSaved} />
          )}
          {mode === "manual" && (
            <ManualPane projectId={projectId} orgTypes={orgTypes} stages={stages} onSaved={onSaved} />
          )}
        </div>
      </div>
    </div>
  );
}

function AISearchPane({ projectId, stages, existingOrgIds, onSaved }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setBusy(true); setError(""); setResults([]);
    try {
      const r = await api.post("/organizations/ai/search", { query: query.trim() }, { timeout: 180000 });
      setResults(r.data.organizations || []);
      if (!r.data.organizations?.length) setError("La IA no encontró organizaciones. Prueba a refinar la consulta.");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const addToPipeline = async (org, stage = "Por contactar") => {
    // Create org first (or upsert)
    const orgRes = await api.post("/organizations", org);
    const orgId = orgRes.data.id;
    if (existingOrgIds.has(orgId)) {
      alert("Esa organización ya está en tu pipeline.");
      return;
    }
    await api.post(`/icaa/projects/${projectId}/pipeline`, { org_id: orgId, stage });
    onSaved?.();
  };

  const QUICK = [
    "Fundaciones documental Europa 2026",
    "Broadcasters internacionales documental autor",
    "Fondos públicos cine España",
    "Plataformas streaming compran documental",
    "Distribuidores documental Europa",
  ];

  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Búsqueda con IA</p>
      <div className="mt-2 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder='Ej. "fundaciones documental Europa 2026"'
          data-testid="ai-search-input"
          className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
        />
        <button onClick={search} disabled={busy || !query.trim()} data-testid="ai-search-go"
                className="rounded-full bg-[#26775E] px-4 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
          {busy ? "Buscando…" : "Buscar"}
        </button>
      </div>

      {results.length === 0 && !busy && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {QUICK.map((q) => (
            <button key={q} onClick={() => { setQuery(q); }}
                    className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] text-stone-700 hover:border-[#26775E] hover:text-[#26775E]">
              {q}
            </button>
          ))}
        </div>
      )}

      {error && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {results.length > 0 && (
        <ul className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1" data-testid="ai-search-results">
          {results.map((o, i) => (
            <li key={i} className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid={`ai-result-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#26775E]">
                    {ORG_TYPE_ICON[o.org_type] || "📎"} {o.org_type}{o.country && ` · ${o.country}`}
                  </p>
                  <p className="mt-0.5 font-serif text-[15px] text-stone-900">{o.name}</p>
                  {o.description && (
                    <p className="mt-1 text-[12.5px] leading-snug text-stone-600">{o.description}</p>
                  )}
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-stone-500">
                    {o.website && (
                      <a href={o.website} target="_blank" rel="noreferrer"
                         className="text-[#26775E] hover:underline">{o.website}</a>
                    )}
                    {o.typical_amount && <span>💰 {o.typical_amount}</span>}
                    {o.deadlines && <span>📅 {o.deadlines}</span>}
                  </p>
                </div>
                <button
                  onClick={() => addToPipeline(o)}
                  data-testid={`ai-result-add-${i}`}
                  className="shrink-0 rounded-full bg-stone-900 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#26775E]"
                >
                  + Añadir
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CatalogPane({ projectId, stages, existingOrgIds, onSaved }) {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/organizations", { params: q ? { q } : {} });
        if (!cancelled) setOrgs(r.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q]);

  const add = async (org) => {
    if (existingOrgIds.has(org.id)) {
      alert("Esa organización ya está en tu pipeline.");
      return;
    }
    await api.post(`/icaa/projects/${projectId}/pipeline`, { org_id: org.id });
    onSaved?.();
  };

  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">
        Catálogo compartido ({orgs.length})
      </p>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por nombre, país, descripción o etiqueta…"
        data-testid="catalog-search"
        className="mt-2 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
      />
      {loading && <p className="mt-4 text-center text-[12px] text-stone-500">Cargando…</p>}
      {!loading && orgs.length === 0 && (
        <p className="mt-4 rounded-md border border-dashed border-stone-300 bg-stone-50 px-3 py-6 text-center text-[12px] text-stone-500">
          El catálogo aún está vacío. Añade organizaciones desde &quot;Buscar con IA&quot; o &quot;Crear manualmente&quot;.
        </p>
      )}
      {!loading && orgs.length > 0 && (
        <ul className="mt-3 max-h-[420px] space-y-1.5 overflow-y-auto pr-1" data-testid="catalog-list">
          {orgs.map((o) => {
            const already = existingOrgIds.has(o.id);
            return (
              <li key={o.id} className="flex items-center gap-3 rounded-md border border-stone-200 bg-white px-3 py-2"
                  data-testid={`catalog-row-${o.id}`}>
                <span className="text-base">{ORG_TYPE_ICON[o.org_type] || "📎"}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-[13.5px] text-stone-900">{o.name}</p>
                  <p className="text-[10.5px] uppercase tracking-widest text-stone-500">
                    {o.org_type}{o.country && ` · ${o.country}`}
                  </p>
                </div>
                <button
                  onClick={() => add(o)}
                  disabled={already}
                  data-testid={`catalog-add-${o.id}`}
                  className="rounded-full bg-stone-900 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#26775E] disabled:bg-stone-300"
                >
                  {already ? "✓ ya en pipeline" : "+ Añadir"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ManualPane({ projectId, orgTypes, stages, onSaved }) {
  const [form, setForm] = useState({
    name: "", org_type: "Fundación", country: "", website: "",
    contact_person: "", email: "", phone: "",
    description: "", typical_amount: "", deadlines: "",
    tags: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!form.name.trim()) { setErr("El nombre es obligatorio."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const orgRes = await api.post("/organizations", payload);
      await api.post(`/icaa/projects/${projectId}/pipeline`, { org_id: orgRes.data.id });
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const fld = (k, label, props = {}) => (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">{label}</span>
      <input
        value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        data-testid={`manual-${k}`}
        className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
        {...props}
      />
    </label>
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {fld("name", "Nombre *")}
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Tipo</span>
          <select value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })}
                  data-testid="manual-org_type"
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]">
            {orgTypes.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        {fld("country", "País")}
        {fld("website", "Web")}
        {fld("contact_person", "Persona de contacto")}
        {fld("email", "Email", { type: "email" })}
        {fld("phone", "Teléfono")}
        {fld("typical_amount", "Importe habitual (ej. 30-100k€)")}
      </div>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Descripción</span>
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="manual-description"
                  className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
      </label>
      {fld("deadlines", "Plazos/convocatorias típicas")}
      {fld("tags", "Etiquetas (separadas por coma)")}

      {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="manual-save"
                className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
          {busy ? "Guardando…" : "Guardar y añadir al pipeline"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Edit modal
// ============================================================================
function EditPipelineModal({ entry, stages, onClose, onSave }) {
  const [data, setData] = useState({ ...entry });
  const org = entry.org || {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:p-6"
         onClick={onClose} data-testid="pipeline-edit-modal">
      <div className="my-4 w-full max-w-2xl rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-stone-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">
              {ORG_TYPE_ICON[org.org_type] || "📎"} {org.org_type}{org.country && ` · ${org.country}`}
            </p>
            <h3 className="mt-0.5 truncate font-display text-xl text-stone-900">{org.name}</h3>
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer"
                 className="text-[11px] text-[#26775E] hover:underline">{org.website}</a>
            )}
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900">✕</button>
        </div>

        <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Fase</span>
            <select value={data.stage} onChange={(e) => setData({ ...data, stage: e.target.value })}
                    data-testid="edit-stage"
                    className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]">
              {stages.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Estimación €</span>
            <input type="number" value={data.estimated_amount || 0}
                   onChange={(e) => setData({ ...data, estimated_amount: Number(e.target.value) || 0 })}
                   data-testid="edit-estimated"
                   className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Confirmado €</span>
            <input type="number" value={data.confirmed_amount || 0}
                   onChange={(e) => setData({ ...data, confirmed_amount: Number(e.target.value) || 0 })}
                   data-testid="edit-confirmed"
                   className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Último contacto</span>
            <input type="date" value={data.last_contact_at || ""}
                   onChange={(e) => setData({ ...data, last_contact_at: e.target.value || null })}
                   data-testid="edit-last-contact"
                   className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Próxima acción</span>
            <input value={data.next_action || ""}
                   onChange={(e) => setData({ ...data, next_action: e.target.value })}
                   data-testid="edit-next-action"
                   placeholder='Ej. "Enviar dossier actualizado y reel"'
                   className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Fecha próxima acción</span>
            <input type="date" value={data.next_action_at || ""}
                   onChange={(e) => setData({ ...data, next_action_at: e.target.value || null })}
                   data-testid="edit-next-action-at"
                   className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Notas internas</span>
            <textarea rows={4} value={data.notes || ""}
                      onChange={(e) => setData({ ...data, notes: e.target.value })}
                      data-testid="edit-notes"
                      placeholder="Conversaciones, contactos, reuniones, condiciones especiales…"
                      className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          </label>
        </div>

        {(entry.history?.length || 0) > 0 && (
          <div className="border-t border-stone-100 px-5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Historial de fases</p>
            <ul className="mt-1 space-y-0.5 text-[11px] text-stone-600">
              {(entry.history || []).map((h, i) => (
                <li key={i}>
                  <span className="font-mono text-stone-400">{(h.at || "").slice(0, 10)}</span>
                  {" — "}
                  <span className="text-stone-400">{h.from || "—"}</span>
                  {" → "}
                  <span className="text-stone-900">{h.to}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-3">
          <button onClick={onClose}
                  className="rounded-full bg-stone-200 px-4 py-2 text-[11px] uppercase tracking-widest text-stone-700 hover:bg-stone-300">
            Cancelar
          </button>
          <button
            onClick={() => onSave({
              stage: data.stage, notes: data.notes, next_action: data.next_action,
              next_action_at: data.next_action_at, last_contact_at: data.last_contact_at,
              estimated_amount: Number(data.estimated_amount) || 0,
              confirmed_amount: Number(data.confirmed_amount) || 0,
            })}
            data-testid="edit-save"
            className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
